import { MoreVertIcon } from '@tunasong/icons'
import { RoomMenu, RoomOverlay } from '@tunasong/plugin-video/ui'
import { Button, type ButtonProps, usePresence, Menu, MenuContent, MenuTrigger } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useCallback, useState } from 'react'
import { useEntityMenuItems } from './entity-menu.hook.js'

import type { Entity, Persisted } from '@tunasong/schemas'
import HelpMenuItem from '../help/help-menu.js'

export interface EntityMenuButtonProps extends ButtonProps {
  className?: string
  entity?: Persisted<Entity>
}

export const EntityMenuButton: FC<EntityMenuButtonProps> = props => {
  const { className, entity, ...buttonProps } = props

  /**
   * Canonical Base UI composition: the menu is uncontrolled and closes itself on
   * item activation, the trigger lives in the tree, and submenu flyouts work. The
   * hook's `onClose`/`onCloseMenu` no longer toggle the menu — they only drive the
   * dialog lifecycle (the menu has already closed on item-press by then).
   */
  const noop = useCallback(() => undefined, [])

  /** @todo should we use presence here? */
  const present = usePresence(entity?.id)

  const { menuItems: entityMenuItems, dialogs: entityDialogs } = useEntityMenuItems({
    entity,
    onClose: noop,
    onCloseMenu: noop,
  })

  /** Room overlay */
  const [showRoomOverlay, setShowRoomOverlay] = useState(false)
  const toggleRoom = useCallback(() => setShowRoomOverlay(o => !o), [])

  return (
    <>
      <Menu>
        <MenuTrigger
          render={
            <Button color="primary" aria-label="Menu Dropdown Button" {...buttonProps}>
              <MoreVertIcon />
            </Button>
          }
        />
        <MenuContent className={className}>
          {...entityMenuItems}

          <RoomMenu open={showRoomOverlay} entityId={entity?.id} onClick={toggleRoom} disabled={present.length === 0} />

          <HelpMenuItem onClose={noop} />
        </MenuContent>
      </Menu>

      {showRoomOverlay ? <RoomOverlay open={showRoomOverlay} entityId={entity?.id} /> : null}
      {...entityDialogs}
    </>
  )
}
